import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { ShowcaseDetailProduct } from 'src/app/api/models/showcases-dto';
import { MoveProductsPayload, ProductTrayDTO, RemoveProductsPayload, TrayDTO } from 'src/app/api/models/trays-dto';
import { TraysService } from 'src/app/api/services/trays.service';
import { ProductStatus } from 'src/app/enums/product-status-enum';
import { TAB } from 'src/app/enums/products.constants';
import { SharedService } from 'src/app/services/shared.service';

@Component({
  selector: 'app-allocation-container',
  templateUrl: './allocation-container.component.html',
  styleUrls: ['./allocation-container.component.scss']
})
export class AllocationContainerComponent implements OnInit {

  @Output() handleMoveProductsFromBasket: EventEmitter<number | string> = new EventEmitter();
  @Output() handleSelectProduct: EventEmitter<ShowcaseDetailProduct> = new EventEmitter();
  @Output() openTrayDetail: EventEmitter<{ tray: TrayDTO, toClone?: boolean }> = new EventEmitter();
  @Output() removeProducts: EventEmitter<RemoveProductsPayload> = new EventEmitter();
  @Output() dropProduct: EventEmitter<MoveProductsPayload> = new EventEmitter();

  @Input() selectedProductsInBasket: string[] = [];
  @Input() isFreeProductsTray!: boolean;
  @Input() highlightedProducts: string = '';
  @Input() productsInBasket: string[] = [];
  @Input() allocationType!: TAB.SHOWCASE | TAB.TRAY | TAB.EXTRA_TRAY;
  @Input() readOnly!: boolean;
  @Input() loading!: boolean;
  @Input() tray!: TrayDTO;

  private readonly STORAGE_KEY = "collapsed-free-products";

  protected dragging: boolean = false;

  public collapsedFreeProducts = false;

  constructor(
    public traysService: TraysService,
    public translateService: TranslateService,
    public sharedService: SharedService
  ) { }

  ngOnInit(): void {
    this.collapsedFreeProducts = this.getCollapsedFreeProductsFromLocalStorage();
  }

  getCode() {
    if (this.traysService.isGenerallyTrayAllocation(this.allocationType)) {
      return this.tray.code
    }
    return this.tray.code || this.translateService.instant('PAGES.ALLOCATION.NOT_ALLOCATED')

  }

  parse(product: ProductTrayDTO): ShowcaseDetailProduct {
    let { sapCode, media, allocations, tray, logisticStatus, extraTrays, isAllocable, isBackup, isBfsHidden, isRepetitive, bookedForSale } = product;
    let parsed: ShowcaseDetailProduct = {
      sapCode: sapCode,
      status: this.getStatus(product),
      logisticStatus: logisticStatus ?? "",
      media: media,
      order: 0,
      allocations: allocations,
      tray: this.traysService.isExtraTrayAllocation(this.allocationType) ? tray : "",
      extraTrays: extraTrays || [],
      isAllocable,
      isBackup,
      isBfsHidden,
      isRepetitive,
      bookedForSale
    }
    return parsed;
  }

  getStatus(product: ProductTrayDTO) {
    let status = undefined;
    switch (true) {
      case this.isFreeProductsTray:
      case this.traysService.isTrayAllocation(this.allocationType):
      case this.traysService.isExtraTrayAllocation(this.allocationType): {
        status = product.allocationStatus;
        break;
      }
      case this.traysService.isShowcaseAllocation(this.allocationType): {
        status = product.status;
        break;
      }
      default: break;
    }
    return (status as ProductStatus);
  }

  onDragStart(event: any) {
    console.log('onDragStart')
    this.dragging = true;
  }

  onDragEnd(event: any) {
    console.log('onDragEnd')
    this.dragging = false;
  }

  drop(event: any) {
    try {
      if (event) {
        const idTray = event.container.element.nativeElement.id;
        const prevIdTray = event.previousContainer.data.id;
        const sapCode = event.previousContainer.data.products[event.previousIndex].sapCode;
        //Sposto il prodotto solo se si troverà in un tray differente
        if (String(prevIdTray) !== String(idTray)) {
          this.dropProduct.emit({
            idTray,
            sapCodes: [sapCode]
          })
        }
      }
    }
    catch (err) {
      console.log(err);
    }
  }

  onover(event: any) {
    if (this.dragging) {
      console.log(event.target.id);
    }
  }

  getProductsCounter() {
    if (this.tray.products && this.tray.products.length > 0) {
      return `(${this.tray.products.length})`
    } else {
      return '';
    }
  }

  isImmovableProduct(product: ProductTrayDTO) {
    if (this.traysService.isShowcaseAllocation(this.allocationType)) {
      return product.status === 2;
    }
    return true;
  }

  openDetail(toClone?: boolean) {
    if (this.isDisabledDetailCta()) {
      return;
    }
    this.openTrayDetail.emit({ tray: this.tray, toClone })
  }

  isDisabledDetailCta() {
    return this.isFreeProductsTray || this.tray.products && this.tray.products?.filter(p => this.productsInBasket.includes(p.sapCode)).length > 0;
  }

  remove(sapCodes: string[]) {
    let payload: RemoveProductsPayload = {
      sapCodes: sapCodes,
      idExtraTray: this.tray.id
    }
    this.removeProducts.emit(payload)
  }

  isHighlighted(sapCode: string) {
    return this.highlightedProducts.includes(sapCode);
  }

  moveProductsHere() {
    switch (true) {
      case this.isFreeProductsTray:
      case this.traysService.isTrayAllocation(this.allocationType):
      case this.traysService.isExtraTrayAllocation(this.allocationType): {
        this.handleMoveProductsFromBasket.emit(this.tray.id)
        break;
      }
      case this.traysService.isShowcaseAllocation(this.allocationType): {
        this.handleMoveProductsFromBasket.emit(this.tray.code)
        break;
      }
      default: break;
    }
  }

  getFormattedReferenceDate(date?: string) {
    if (date) {
      return moment(date).format("DD-MM-YYYY");
    } else {
      return "";
    }
  }

  getTrayLocation(): string {
    const { location } = this.tray;
    if (location) {
      return `${location} - `;
    }
    return '';
  }

  selectProduct(product: ShowcaseDetailProduct) {
    this.handleSelectProduct.emit({
      ...product,
      prevAllocationCode: this.tray.code
    })
  }

  collapseFreeProducts() {
    this.collapsedFreeProducts = !this.collapsedFreeProducts;
    this.setCollapsedFreeProductsInLocalStorage();
  }

  setCollapsedFreeProductsInLocalStorage() {
    localStorage.setItem(this.STORAGE_KEY, this.collapsedFreeProducts.toString());
  }

  getCollapsedFreeProductsFromLocalStorage() {
    return Boolean(localStorage.getItem(this.STORAGE_KEY));
  }

}
